'use client';
import { useState, useEffect } from 'react';
import Image from 'next/image';
import { Play, Pause } from 'lucide-react';
import { useVideoViewport } from '@/hooks/useVideoViewport';

const STORAGE_KEY = 't3k-hero-video-playing';

export const HeroVideo = ({
  src = '/hero/hero.mp4',
  poster = '/hero/hero.png',
  storageKey = STORAGE_KEY,
}: {
  src?: string;
  poster?: string;
  storageKey?: string;
}) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useVideoViewport({
    threshold: 0.1,
    playImmediately: true,
  });

  const handAutoPlay = () => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
  };

  // Load preference from localStorage on mount and handle video play events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const savedPreference = localStorage.getItem(storageKey);
    const shouldPlay = savedPreference === null ? true : savedPreference === 'true';
    setIsPlaying(shouldPlay);

    // If user preference is to pause, wait for the video to start playing then pause it
    if (!shouldPlay) {
      video.addEventListener('play', handAutoPlay);

      return () => {
        video.removeEventListener('play', handAutoPlay);
      };
    }
  }, [handAutoPlay, storageKey]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
        localStorage.setItem(storageKey, 'false');
      } else {
        window.removeEventListener('play', handAutoPlay);
        videoRef.current.play();
        setIsPlaying(true);
        localStorage.setItem(storageKey, 'true');
      }
    }
  };

  return (
    <>
      {/* Next.js optimized poster image - always present as background */}
      <Image
        src={poster}
        alt="Hero background"
        fill
        className="absolute inset-0 object-cover object-center z-0"
        sizes="100vw"
        priority={true}
      />

      {/* Video overlays the image when it loads */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover object-center z-10"
        muted
        loop
        playsInline
      >
        <source src={src} type="video/mp4" />
      </video>

      {/* Play/Pause button */}
      <button
        onClick={togglePlayPause}
        className="absolute bottom-4 right-4 z-20 p-2 hidden md:block"
        aria-label={isPlaying ? 'Pause video' : 'Play video'}
      >
        {isPlaying ? (
          <Pause className="w-4 h-4 text-white" />
        ) : (
          <Play className="w-4 h-4 text-white" />
        )}
      </button>
    </>
  );
};
